import type { StakeholderType } from '../types/user'
import { api } from './fetch'

export type LoginParams = {
  password: string
  email: string
}

export type RegisterParams = {
  email: string
  password: string
  first_name: string
  last_name: string
  phone_number: string
  stakeholder_types: StakeholderType[]
  company_name: string
  company_address: string
  company_type?: string
  ccn?: string
  eid?: string
  license_number?: string
  permit_number?: string
  zoning_number?: string
  promo_code?: string
  payment_method_id?: string
}

export const DAYDACO_AUTH_KEY = 'daydaco.authkey'

// Axios errors carry the full request config (Authorization header, request
// body), so never log them raw — extract only non-secret fields
const sanitizeAuthError = (e: unknown) => {
  if (e && typeof e === 'object') {
    const err = e as {
      message?: string
      code?: string
      response?: { status?: number }
    }
    return {
      status: err.response?.status,
      code: err.code,
      message: err.message,
    }
  }
  return { message: String(e) }
}

export const loginService = async ({ password, email }: LoginParams) => {
  try {
    const res = await api.post('/accounts/app/v1/auth/login/', {
      password,
      email,
    })

    const data = res.data
    const sessionToken = data.meta.session_token
    window.localStorage.setItem(DAYDACO_AUTH_KEY, sessionToken)
    return data
  } catch (e) {
    return { error: e }
  }
}

export const registerService = async (data: RegisterParams) => {
  try {
    const res = await api.post('/accounts/app/v1/auth/signup/', data)

    // Don't auto-login after registration since email verification is required
    return res.data
  } catch (e) {
    console.error('Registration error:', sanitizeAuthError(e))
    return { error: e }
  }
}

export const verifyEmailOtpService = async (key: string) => {
  try {
    const res = await api.post('/accounts/app/v1/auth/email/verify/', {
      key: key,
    })

    // Store the session token from AllAuth format (meta.session_token)
    if (res.data?.meta?.session_token) {
      window.localStorage.setItem(DAYDACO_AUTH_KEY, res.data.meta.session_token)
      console.log('✓ AllAuth session token stored after email verification')
    } else {
      console.warn('⚠️ No AllAuth session token found in verification response')
    }

    return res.data
  } catch (e) {
    console.error('Email verification error:', sanitizeAuthError(e))
    return { error: e }
  }
}

export const sendEmailOtpService = async (email: string) => {
  try {
    const sessionToken = window.localStorage.getItem(DAYDACO_AUTH_KEY)
    if (!sessionToken) {
      console.error('❌ sendEmailOtpService: No session token found')
      return { error: new Error('No session token available') }
    }

    // Use the standard API client which will add Bearer token automatically
    const res = await api.post('/accounts/app/v1/auth/email/send/', {
      email,
    })

    return res.data
  } catch (e) {
    console.error('❌ sendEmailOtpService error:', sanitizeAuthError(e))
    return { error: e }
  }
}

export const resendEmailOtpService = async (email: string) => {
  try {
    // Use the standard API client which will add Bearer token automatically
    const res = await api.post('/accounts/app/v1/auth/email/resend/', {
      email,
    })
    return res.data
  } catch (e) {
    console.error('Resend OTP error:', sanitizeAuthError(e))
    return { error: e }
  }
}

export const getUserService = async () => {
  try {
    // Use the standard API client which will add Bearer token automatically
    const res = await api.get('/accounts/app/v1/auth/session/')

    const data = res.data

    if (!data.error && data.data?.user?.id) {
      // Fetch full user details
      const userRes = await api.get(`/user/${data.data.user.id}`)
      data.data.user = userRes.data
    }
    return data
  } catch (e) {
    console.error('❌ getUserService error:', sanitizeAuthError(e))
    return { error: e }
  }
}

export const requestPasswordResetService = async (email: string) => {
  try {
    // Try the most likely endpoint for requesting password reset email
    const url = `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api'}/accounts/app/v1/auth/password/request/`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // NO Authorization header for unauthenticated users
      },
      body: JSON.stringify({ email }),
    })

    const data = await response.json()

    if (response.ok) {
      return data
    } else {
      console.error(
        '❌ requestPasswordResetService: Password reset request failed with status:',
        response.status,
      )
      return { error: { response: { data, status: response.status } } }
    }
  } catch (e) {
    console.error('❌ requestPasswordResetService: Network/fetch error:', e)
    return { error: e }
  }
}

export const validatePasswordResetKeyService = async (key: string) => {
  try {
    const url = `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api'}/accounts/app/v1/auth/password/reset/`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Password-Reset-Key': key,
        // NO Authorization header for unauthenticated users
      },
    })

    const data = await response.json()

    if (response.ok) {
      return data
    } else {
      console.error(
        '❌ validatePasswordResetKeyService: Key validation failed with status:',
        response.status,
      )
      return { error: { response: { data, status: response.status } } }
    }
  } catch (e) {
    console.error('❌ validatePasswordResetKeyService: Network/fetch error:', e)
    return { error: e }
  }
}

export const confirmPasswordResetService = async (
  key: string,
  password: string,
) => {
  try {
    const url = `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api'}/accounts/app/v1/auth/password/reset/`

    const payload = {
      key,
      password, // Single password field as per API spec
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // NO Authorization header for unauthenticated users
      },
      body: JSON.stringify(payload),
    })

    const data = await response.json()

    if (response.ok) {
      return data
    } else if (response.status === 401) {
      // Password reset succeeded but the user needs to login manually
      return { success: true, requiresLogin: true, data }
    } else {
      console.error(
        '❌ confirmPasswordResetService: Password reset confirmation failed with status:',
        response.status,
      )
      return { error: { response: { data, status: response.status } } }
    }
  } catch (e) {
    console.error('❌ confirmPasswordResetService: Network/fetch error:', e)
    return { error: e }
  }
}
